// Types for spending analytics and insights

import { Partner } from './database';

export interface SpendingAnalyticsRequest {
  couple_id: string;
  period?: 'week' | 'month' | 'quarter' | 'year';
  months_back?: number;
  start_date?: string;
  end_date?: string;
}

export interface SpendingAnalyticsResponse {
  success: boolean;
  data?: SpendingAnalyticsData;
  error?: string;
}

export interface SpendingAnalyticsData {
  overview: SpendingOverviewData;
  categoryBreakdown: CategoryBreakdownData[];
  trends: SpendingTrendsData[];
  partnerComparison: PartnerComparisonData;
  categoryTrends: CategoryTrendsData[];
}

export interface SpendingOverviewData {
  currentPeriod: {
    amount: number;
    count: number;
    period: string;
    startDate: string;
    endDate: string;
  };
  previousPeriod: {
    amount: number;
    count: number;
    period: string;
    startDate: string;
    endDate: string;
  };
  percentageChange: number;
  averageDaily: number;
  daysInPeriod: number;
  daysRemaining?: number;
}

export interface CategoryBreakdownData {
  categoryId: string;
  categoryName: string;
  categoryIcon: string;
  categoryColor: string;
  amount: number;
  count: number;
  percentage: number;
  previousPeriodAmount: number;
  percentageChange: number;
}

export interface SpendingTrendsData {
  period: string;
  date: string;
  amount: number;
  count: number;
  partner1Amount: number;
  partner2Amount: number;
  topCategory?: {
    name: string;
    amount: number;
    icon: string;
    color: string;
  };
}

export interface PartnerComparisonData {
  partner1: PartnerSpendingData;
  partner2: PartnerSpendingData;
  totalAmount: number;
  partner1Percentage: number;
  partner2Percentage: number;
}

export interface PartnerSpendingData {
  name: string;
  amount: number;
  count: number;
  averagePerTransaction: number;
  topCategory?: {
    name: string;
    amount: number;
    icon: string;
    color: string;
  };
  categories: Array<{
    categoryName: string;
    categoryIcon: string;
    categoryColor: string;
    amount: number;
    count: number;
  }>;
}

export interface CategoryTrendsData {
  categoryId: string;
  categoryName: string;
  categoryIcon: string;
  categoryColor: string;
  currentAmount: number;
  previousAmount: number;
  percentageChange: number;
  trend: 'up' | 'down' | 'stable';
  currentCount: number;
  previousCount: number;
}

// Insight generation types
export interface GenerateInsightsRequest {
  couple_id: string;
  period?: 'week' | 'month' | 'quarter';
}

export interface GenerateInsightsResponse {
  success: boolean;
  data?: InsightData[];
  error?: string;
}

export interface InsightData {
  id: string;
  title: string;
  description: string;
  type: 'positive' | 'warning' | 'neutral' | 'tip';
  category?: string;
  icon: string;
  priority: number;
  metadata?: {
    amount?: number;
    percentage?: number;
    category?: string;
    partner?: string;
    trend?: 'up' | 'down' | 'stable';
  };
}

// Chart data types for frontend components
export interface ChartDataPoint {
  label: string;
  value: number;
  color?: string;
  metadata?: Record<string, any>;
}

export interface PieChartData {
  data: ChartDataPoint[];
  total: number;
}

export interface LineChartData {
  data: Array<{
    label: string;
    value: number;
    partner1?: number;
    partner2?: number;
    date: string;
  }>;
  maxValue: number;
  minValue: number;
}

export interface BarChartData {
  data: Array<{
    label: string;
    current: number;
    previous: number;
    change: number;
    changeType: 'up' | 'down' | 'stable';
  }>;
  maxValue: number;
}

// Filters and options
export interface AnalyticsFilters {
  period: 'week' | 'month' | 'quarter' | 'year';
  monthsBack: number;
  categoryIds?: string[];
  partners?: Partner[];
  minAmount?: number;
  maxAmount?: number;
}

export interface ChartOptions {
  showAnimation?: boolean;
  showTooltips?: boolean;
  showLegend?: boolean;
  height?: number;
  colors?: string[];
  responsive?: boolean;
}

// Constants
export const ANALYTICS_PERIODS = {
  WEEK: 'week',
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year'
} as const;

export const INSIGHT_TYPES = {
  POSITIVE: 'positive',
  WARNING: 'warning',
  NEUTRAL: 'neutral',
  TIP: 'tip'
} as const;

export const TREND_DIRECTIONS = {
  UP: 'up',
  DOWN: 'down',
  STABLE: 'stable'
} as const;

export const DEFAULT_ANALYTICS_FILTERS: AnalyticsFilters = {
  period: 'month',
  monthsBack: 3
};

export const DEFAULT_CHART_OPTIONS: ChartOptions = {
  showAnimation: true,
  showTooltips: true,
  showLegend: true,
  height: 300,
  responsive: true
};